import React, { useEffect, useRef } from "react";
import { CheckboxSkeleton } from "./../skeletons/checkbox-skeleton";
import { MAX_SENTENCES } from "../../../../lib/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilteredLanguages,
  selectLanguagesData,
  selectLanguagesLoading,
  selectPurchasedLanguages,
} from "../../../../redux/selectors/languageSelectors";
import { selectPurchasedBookIds } from "../../../../redux/selectors/hasPaidSelectors";
import { selectQuickFilter } from "../../../../redux/selectors/filterSelectors";
import {
  deselectAllLanguages,
  selectAllLanguages,
  selectOnlyLanguages,
  toggleSelectedLanguage,
} from "../../../../redux/slices/filterSlice";
import { Badge } from "../../../../components/ui/badge";
import { Label } from "../../../../components/ui/label";
import { Checkbox } from "../../../../components/ui/checkbox";
import { cn, formatLanguageName } from "../../../../lib/utils";
import { useLoggedIn } from "../../../../hooks/use-logged-in";
import { Link } from "react-router-dom";
import { Button } from "../../../../components/ui/button";

function LanguageCheckboxes() {
  const dispatch = useDispatch();
  const languages = useSelector(selectLanguagesData);
  const loading = useSelector(selectLanguagesLoading);
  const purchasedLanguageIds = useSelector(selectPurchasedLanguages).map(
    (item) => item.id
  );
  const quickFilter = useSelector(selectQuickFilter);
  const { isLoggedIn } = useLoggedIn();
  const purchasedBookIds = useSelector(selectPurchasedBookIds);

  const {
    languageSearchQuery,
    currentPage,
    itemsPerPage,
    selectedLanguages,
    chapters,
  } = useSelector((state) => state.filters);

  const filteredLanguages = useSelector((state) =>
    selectFilteredLanguages(state, languageSearchQuery)
  );
  const didSetDefaultsRef = useRef(false);

  useEffect(() => {
    if (didSetDefaultsRef.current) {
      return;
    }
    if (isLoggedIn && purchasedLanguageIds.length > 0) {
      const purchasedNames = languages
        .filter((lang) => purchasedLanguageIds.includes(lang.id))
        .map((lang) => lang.name);
      if (purchasedNames.length > 0) {
        dispatch(selectOnlyLanguages(purchasedNames));
        didSetDefaultsRef.current = true;
      }
    }
  }, [dispatch, isLoggedIn, languages, purchasedLanguageIds]);

  const selectableLanguages = filteredLanguages;

  return (
    <div
      className="polyglot-language-list flex flex-col gap-1 py-3 px-3 max-h-[320px] md:max-h-[400px] lg:h-[320px] overflow-y-auto overflow-x-clip w-full rounded-xl bg-white border border-input drop-shadow-sm"
      style={{
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(96, 165, 250, 0.75) rgba(134, 239, 173, 0.25)",
      }}
    >
      <div
        className={cn(
          "flex flex-col gap-2 items-start py-2 w-full"
        )}
      >
        {filteredLanguages.length === 0 && !loading ? (
          quickFilter === "purchased" && purchasedLanguageIds.length === 0 ? (
            <div className="flex flex-col gap-2 text-gray-600">
              <p>No purchased languages yet.</p>
              <Link
                to={isLoggedIn ? "/pricing" : "/get-started?tab=login"}
                className="inline-flex"
              >
                <Button variant="secondary" size="sm">
                  {isLoggedIn ? "Purchase Languages" : "Login to Purchase"}
                </Button>
              </Link>
            </div>
          ) : (
            <p className="text-gray-600">No match.</p>
          )
        ) : (
          <>
            <div className="flex items-center gap-3 py-2 px-1 rounded-lg w-full">
              <Checkbox
                id={`select-all`}
                checked={
                  selectableLanguages.length > 0 &&
                  selectableLanguages.every((language) =>
                    selectedLanguages.includes(language.name)
                  )
                }
                onCheckedChange={() =>
                  selectableLanguages.length > 0 &&
                  selectableLanguages.every((language) =>
                    selectedLanguages.includes(language.name)
                  )
                    ? dispatch(deselectAllLanguages())
                    : dispatch(selectAllLanguages(selectableLanguages))
                }
                disabled={loading}
              />

              <Label
                htmlFor={`select-all`}
                className="flex-1 cursor-pointer font-medium whitespace-nowrap"
              >
                Select All
              </Label>
            </div>
          </>
        )}
      </div>
      {loading ? (
        <CheckboxSkeleton count={6} />
      ) : (
        filteredLanguages?.map((lang, index) => {
          const viewingLimitedSentences =
            currentPage * itemsPerPage <= MAX_SENTENCES;
          const isUnpurchasedLanguage = !purchasedLanguageIds.includes(lang.id);

          const unpurchasedBooks = chapters.filter(
            (item) => !purchasedBookIds.includes(item.book_id)
          );
          const onlyFirstChapters = unpurchasedBooks.every(
            (item) => item.chapter_id === 1
          );

          // Apply the rules
          let shouldBeDisabled = false;

          // Unpurchased languages should only see chapter 1
          if (isUnpurchasedLanguage && !onlyFirstChapters) {
            shouldBeDisabled = true;
          }

          // Unpurchased languages can be viewed within the limit
          if (viewingLimitedSentences) {
            shouldBeDisabled = false;
          }


          return (
            <div
              key={index}
              className={cn(
                "flex items-center gap-3 py-2 px-1 rounded-lg transition-colors duration-100 ease-in w-full min-w-0",
                shouldBeDisabled ? "cursor-not-allowed" : "cursor-pointer"
              )}
              onMouseEnter={() => {
                // find if sentences already exist
                // find book_id from books where name is lang.name
              }}
              onClick={(e) => {
                if (e.target.nodeName === "DIV") {
                  if (!shouldBeDisabled) {
                    dispatch(toggleSelectedLanguage(lang.name));
                  }
                }
              }}
            >
              <Checkbox
                id={`language-${index}`}
                className={`text-[${languages[index]?.textColor}]`}
                checked={selectedLanguages.includes(lang.name)}
                onCheckedChange={() =>
                  dispatch(toggleSelectedLanguage(lang.name))
                }
                disabled={shouldBeDisabled}
              />

              <Label
                htmlFor={`language-${index}`}
                className="flex-1 cursor-pointer font-medium break-words min-w-0"
              >
                {formatLanguageName(lang.name)}
              </Label>
              {purchasedLanguageIds.includes(lang.id) ? (
                <Badge className="rounded-full shrink-0">Purchased</Badge>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}

export default LanguageCheckboxes;
